import axiosInstance from 'src/common/axiosInstance';

// AUTO-CRM-32: reads /analytics/crm-overview for every pipeline and combines
// them into one workspace picture for Home and Insights. Each pipeline loads
// on its own, so one failing pipeline never blanks the page.

export const QUEUE_ORDER = ['overdue', 'due_today', 'unassigned', 'untouched', 'without_next_activity', 'stale', 'today'];

export const QUEUE_COPY = {
  overdue: { label: 'Overdue follow-ups', hint: 'Promised calls and meetings that slipped', tone: 'red' },
  due_today: { label: 'Due today', hint: 'Follow-ups scheduled for today', tone: 'amber' },
  unassigned: { label: 'Need an owner', hint: 'Open leads nobody is working', tone: 'amber' },
  untouched: { label: 'Never contacted', hint: 'No call, message or note yet', tone: 'blue' },
  without_next_activity: { label: 'No next step', hint: 'Open leads without a scheduled follow-up', tone: 'blue' },
  stale: { label: 'Gone quiet', hint: 'No activity in 14 days', tone: 'blue' },
  today: { label: 'New today', hint: 'Leads that arrived today', tone: 'green' },
};

export const ACTIVITY_LABELS = { CALL: 'Call', EMAIL: 'Email', WHATSAPP: 'WhatsApp', MEETING: 'Meeting', NOTE: 'Note', FOLLOWUP: 'Follow-up' };

export async function fetchPipelineInsights(pipelineId, { days = 30, mode = 'full' } = {}) {
  const response = await axiosInstance.get('/analytics/crm-overview', { params: { pipeline_id: pipelineId || undefined, days, mode } });
  return response.data?.data || null;
}

export async function fetchWorkspaceInsights(pipelines, options) {
  const results = await Promise.allSettled(pipelines.map((pipeline) => fetchPipelineInsights(pipeline.id, options)));
  return pipelines.map((pipeline, index) => ({
    pipeline,
    data: results[index].status === 'fulfilled' ? results[index].value : null,
    error: results[index].status === 'rejected' ? results[index].reason : null,
  }));
}

const sum = (rows, pick) => rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);

export function combineInsights(rows) {
  const loaded = rows.filter((row) => row.data);
  if (!loaded.length) return null;
  const metric = (key) => sum(loaded, (row) => row.data.metrics?.[key]);
  const newLeads = metric('new_leads');
  // Rebuild the previous period from each pipeline's change so the combined change is exact.
  const previousNew = sum(loaded, (row) => {
    const current = Number(row.data.metrics?.new_leads) || 0;
    const change = row.data.metrics?.new_leads_change_percent;
    if (change === null || change === undefined) return current ? NaN : 0;
    return current / (1 + Number(change) / 100);
  });
  const totalLeads = metric('total_leads');
  const closed = metric('closed_leads');
  const activities = metric('period_activities');
  const completed = metric('completed_activities');
  const won = sum(loaded, (row) => (row.data.funnel || []).filter((stage) => stage.is_closed && Number(stage.probability) >= 100).reduce((total, stage) => total + stage.count, 0));
  const lost = sum(loaded, (row) => (row.data.funnel || []).filter((stage) => stage.is_closed && Number(stage.probability) < 100).reduce((total, stage) => total + stage.count, 0));

  const queues = [];
  loaded.forEach((row) => {
    Object.entries(row.data.queues || {}).forEach(([key, queue]) => {
      if (!queue?.count) return;
      queues.push({ key, count: queue.count, leadIds: queue.lead_ids || [], label: QUEUE_COPY[key]?.label || queue.label, pipeline: row.pipeline });
    });
  });
  queues.sort((a, b) => QUEUE_ORDER.indexOf(a.key) - QUEUE_ORDER.indexOf(b.key) || b.count - a.count);

  // One row per queue across pipelines; it opens the pipeline with the most leads in it.
  const queueGroups = [];
  queues.forEach((queue) => {
    const group = queueGroups.find((item) => item.key === queue.key);
    if (group) { group.count += queue.count; group.parts.push(queue); } else queueGroups.push({ key: queue.key, label: queue.label, count: queue.count, parts: [queue], top: queue });
  });

  const upcoming = loaded.flatMap((row) => (row.data.upcoming_activities || []).map((activity) => ({ ...activity, pipeline: row.pipeline })))
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const recent = loaded.flatMap((row) => (row.data.recent_activities || []).map((activity) => ({ ...activity, pipeline: row.pipeline })))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const teamById = new Map();
  loaded.forEach((row) => (row.data.team_performance || []).forEach((member) => {
    const id = member.user?.id;
    if (!id) return;
    const current = teamById.get(id) || { user: member.user, role: member.role, assigned_leads: 0, activities: 0, completed_activities: 0, leads_touched: 0, nodes_completed: 0 };
    ['assigned_leads', 'activities', 'completed_activities', 'leads_touched', 'nodes_completed'].forEach((key) => { current[key] += Number(member[key]) || 0; });
    teamById.set(id, current);
  }));
  const team = [...teamById.values()].map((member) => ({ ...member, completion_rate: member.activities ? Math.round((member.completed_activities / member.activities) * 100) : 0 }))
    .sort((a, b) => b.completed_activities - a.completed_activities || b.activities - a.activities);

  const attention = {};
  loaded.forEach((row) => Object.entries(row.data.attention || {}).forEach(([key, value]) => { attention[key] = (attention[key] || 0) + (Number(value) || 0); }));

  const trendByDate = new Map();
  loaded.forEach((row) => (row.data.trend || []).forEach((point) => {
    const current = trendByDate.get(point.date) || { date: point.date, new_leads: 0, activities: 0 };
    current.new_leads += Number(point.new_leads) || 0;
    current.activities += Number(point.activities) || 0;
    trendByDate.set(point.date, current);
  }));

  const sources = new Map();
  loaded.forEach((row) => (row.data.source_performance || []).forEach((source) => {
    const current = sources.get(source.source) || { source: source.source, leads: 0, closed_leads: 0 };
    current.leads += Number(source.leads) || 0;
    current.closed_leads += Number(source.closed_leads) || 0;
    sources.set(source.source, current);
  }));

  const mix = new Map();
  loaded.forEach((row) => (row.data.activity_mix || []).forEach((item) => mix.set(item.type, (mix.get(item.type) || 0) + (Number(item.count) || 0))));

  return {
    period: loaded[0].data.period,
    partial: loaded.length < rows.length,
    metrics: {
      total_leads: totalLeads,
      open_leads: totalLeads - closed,
      new_leads: newLeads,
      new_leads_change_percent: Number.isFinite(previousNew) && previousNew > 0 ? Math.round(((newLeads - previousNew) / previousNew) * 1000) / 10 : null,
      closed_leads: closed,
      won_leads: won,
      lost_leads: lost,
      win_rate: won + lost ? Math.round((won / (won + lost)) * 1000) / 10 : null,
      period_activities: activities,
      completed_activities: completed,
      activity_completion_rate: activities ? Math.round((completed / activities) * 1000) / 10 : 0,
      overdue_activities: metric('overdue_activities'),
      due_today: metric('due_today'),
    },
    attention,
    queues,
    queueGroups,
    upcoming,
    recent,
    team,
    trend: [...trendByDate.values()].sort((a, b) => a.date.localeCompare(b.date)),
    sources: [...sources.values()].map((source) => ({ ...source, closed_rate: source.leads ? Math.round((source.closed_leads / source.leads) * 1000) / 10 : 0 })).sort((a, b) => b.leads - a.leads),
    activityMix: [...mix.entries()].map(([type, count]) => ({ type, count })),
    pipelines: rows.map((row) => ({ pipeline: row.pipeline, data: row.data, error: row.error })),
  };
}

// Opens the lead list on a pipeline with a saved queue (see Leads.jsx `leadQueue`).
export function leadQueueState(queue) {
  return { leadQueue: { pipelineId: queue.pipeline?.id, key: queue.key, label: `${queue.label} · ${queue.pipeline?.name || ''}`.trim(), leadIds: queue.leadIds } };
}

export function formatPercent(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`;
}
